export const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_term_sbs',
] as const;

export type UtmKey = (typeof UTM_KEYS)[number];
export type UTMParams = Partial<Record<UtmKey, string>>;

/**
 * Persist UTM query params from the current page URL into sessionStorage.
 * Overwrites a key only when the URL contains a non-empty value (last-touch per key).
 */
export function storeUTMFromUrl(): void {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);

  UTM_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) {
      sessionStorage.setItem(key, value);
    }
  });
}

/** Return stored UTM values (empty keys omitted). */
export function getStoredUTM(): UTMParams {
  if (typeof window === 'undefined') return {};

  const result: UTMParams = {};

  UTM_KEYS.forEach((key) => {
    const value = sessionStorage.getItem(key);
    if (value) {
      result[key] = value;
    }
  });

  return result;
}

/**
 * Append stored UTM params to a form iframe URL.
 * Idempotent: does not overwrite params already present on the href.
 */
export function appendUTMToUrl(href: string): string {
  if (!href || href === '#') return href;

  const stored = getStoredUTM();
  if (!Object.keys(stored).length) return href;

  try {
    const url = new URL(href, window.location.origin);

    UTM_KEYS.forEach((key) => {
      const value = stored[key];
      if (value && !url.searchParams.has(key)) {
        url.searchParams.set(key, value);
      }
    });

    // Preserve relative hrefs when the input was relative
    if (/^https?:\/\//i.test(href)) {
      return url.toString();
    }

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return href;
  }
}
